import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clearGymStorage } from './gym-store';

export interface User {
  userId: string;
  email: string;
  fullName?: string;
  phoneNumber?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER';
  dateOfBirth?: string;
  role?: 'CUSTOMER' | 'GYM_OWNER' | 'SUPER_ADMIN';
  roles?: Array<'CUSTOMER' | 'GYM_OWNER' | 'SUPER_ADMIN'>;
  onboardingComplete?: boolean;
  customerOnboardingComplete?: boolean;
}

interface UserState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  _hasHydrated: boolean;
  isInitializing: boolean;
  setAuth: (data: { user: User; accessToken: string; refreshToken?: string }) => void;
  setTokens: (data: { accessToken: string; refreshToken?: string }) => void;
  setUser: (user: User) => void;
  clearAuth: () => void;
  setHasHydrated: (state: boolean) => void;
  setIsInitializing: (state: boolean) => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      _hasHydrated: false,
      isInitializing: true,

      setAuth: ({ user, accessToken, refreshToken }) =>
        set({
          user,
          accessToken,
          refreshToken: refreshToken ?? null,
          isAuthenticated: true,
        }),

      setTokens: ({ accessToken, refreshToken }) =>
        set((state) => ({
          accessToken,
          refreshToken: refreshToken ?? state.refreshToken,
          isAuthenticated: !!accessToken,
        })),

      setUser: (user) =>
        set({
          user,
        }),

      clearAuth: () => {
        // Clear gym storage on logout
        clearGymStorage();

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      setHasHydrated: (state) => set({ _hasHydrated: state }),
      setIsInitializing: (state) => set({ isInitializing: state }),
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
